import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';
import { User } from '../../types';
import '../../styles/shared.css';
import './ProfilePage.css';

const ProfilePage: React.FC = () => {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<User>({
    id: 0,
    username: '',
    phone: '',
    role: 'user',
    status: 'active',
    createdAt: ''
  });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    phone: '',
    realName: '',
    avatar: '',
    grade: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const data = await authAPI.getProfile();
      setProfile(data);
      setFormData({
        phone: data.phone || '',
        realName: data.realName || '',
        avatar: data.avatar || '',
        grade: data.grade || ''
      });
    } catch (error) {
      console.error('获取用户信息失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await authAPI.updateProfile(formData);
      setProfile(prev => ({ ...prev, ...formData }));
      setEditing(false);
      alert('保存成功');
    } catch (error) {
      console.error('保存失败:', error);
      alert('保存失败');
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // For now, just create a preview URL. 
    // TODO: Implement actual avatar upload endpoint
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, avatar: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  if (loading) return <div className="loading">加载中...</div>;

  return (
    <div className="page-container profile-page">
      <div className="page-header">
        <div className="header-content">
          <h1>个人资料</h1>
        </div>
      </div>
      
      <div className="content-container">
        <div className="profile-card">
          <div className="avatar-section">
            <div className="avatar-preview">
              <img 
                src={formData.avatar || "https://img01.yzcdn.cn/vant/cat.jpeg"} 
                alt="用户头像" 
              />
            </div>
            {editing && (
              <div className="avatar-upload">
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageUpload}
                  className="file-input"
                />
                <button className="btn btn-secondary">更换头像</button>
              </div>
            )}
          </div>

          <div className="info-section">
            <div className="info-item">
              <label className="info-label">用户名</label>
              <div className="info-value">{profile.username}</div>
            </div>
            
            <div className="info-item">
              <label className="info-label">学生姓名</label>
              {editing ? (
                <input 
                  type="text" 
                  value={formData.realName}
                  onChange={(e) => setFormData(prev => ({ ...prev, realName: e.target.value }))}
                  className="edit-input"
                  placeholder="请输入学生姓名"
                />
              ) : (
                <div className="info-value">{profile.realName || '未填写'}</div>
              )}
            </div>

            <div className="info-item">
              <label className="info-label">年级</label>
              {editing ? (
                <select 
                  value={formData.grade}
                  onChange={(e) => setFormData(prev => ({ ...prev, grade: e.target.value }))}
                  className="edit-input"
                >
                  <option value="">请选择年级</option>
                  <option value="一年级">一年级</option>
                  <option value="二年级">二年级</option>
                  <option value="三年级">三年级</option>
                  <option value="四年级">四年级</option>
                  <option value="五年级">五年级</option>
                  <option value="六年级">六年级</option>
                  <option value="初一">初一</option>
                  <option value="初二">初二</option>
                  <option value="初三">初三</option>
                  <option value="高一">高一</option>
                  <option value="高二">高二</option>
                  <option value="高三">高三</option>
                </select>
              ) : (
                <div className="info-value">{profile.grade || '未填写'}</div>
              )}
            </div>

            <div className="info-item">
              <label className="info-label">手机号</label>
              {editing ? (
                <input 
                  type="tel" 
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="edit-input"
                  placeholder="请输入手机号"
                />
              ) : (
                <div className="info-value">{profile.phone || '未填写'}</div>
              )}
            </div>
          </div>

          <div className="action-section">
            {editing ? (
              <div className="action-buttons">
                <button 
                  className="btn btn-primary" 
                  onClick={handleSave}
                >
                  保存
                </button>
                <button 
                  className="btn btn-tertiary" 
                  onClick={() => {
                    setEditing(false);
                    setFormData({
                      phone: profile.phone || '',
                      realName: profile.realName || '',
                      avatar: profile.avatar || '',
                      grade: profile.grade || ''
                    });
                  }}
                >
                  取消
                </button>
              </div>
            ) : (
              <div className="action-buttons">
                <button 
                  className="btn btn-primary" 
                  onClick={() => setEditing(true)}
                >
                  编辑资料
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => navigate('/my/change-password')}
                >
                  修改密码
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;